import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { UserService } from '../../../services/user.service';
import { Job } from '../../../model/job.model';
import { ToolbarService } from '../../../services/toolbar.service';
import { MatDialog } from '@angular/material/dialog';
import { UpdateRepairTimelineComponent } from '../update-repair-timeline/update-repair-timeline.component';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { filter, first } from 'rxjs/operators';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { mediumModalConfig } from '../../../consts/modal-config.const';
import { CurrentUserService } from '../../../services/currentUser.service';
import { JobService } from '../../../services/job.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent } from '@angular/material/card';
import { RouterLink } from '@angular/router';
import { DatePipe, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { CarPlaceholderOrPhotoComponent } from '../../../components/car-placeholder-or-photo/car-placeholder-or-photo.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-repairer-tile',
  templateUrl: './repairer-tile.component.html',
  styleUrls: ['./repairer-tile.component.scss'],
  imports: [
    MatCard,
    RouterLink,
    MatCardContent,
    NgClass,
    MatIcon,
    CarPlaceholderOrPhotoComponent,
    MatIconButton,
    MatMenuTrigger,
    MatButton,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    DatePipe
  ]
})
export class RepairerTileComponent implements OnInit, AfterViewInit, AfterContentChecked {
  job = input.required<Job>();
  hasRedBadge = input(false);
  bucket = input.required<string>();
  getJobs = output<void>();
  vehicleDescription = viewChild<ElementRef>('vehicleDescription');
  vehicleDescriptionText = viewChild<ElementRef>('vehicleDescriptionText');

  setAnimation = signal<boolean>(false);
  activeJobId = signal<number | undefined>(undefined);

  #matDialog = inject(MatDialog);
  #destroyRef = inject(DestroyRef);
  #elRef = inject(ElementRef);
  #currentUserService = inject(CurrentUserService);
  #jobService = inject(JobService);
  #message = inject(MessagingService);
  #toolbarService = inject(ToolbarService);
  userService = inject(UserService);
  #modalConfigsHelper = inject(ModalConfigsHelper);

  ngOnInit(): void {
    this.#jobService.activeJobId$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((activeJobId) => {
      this.activeJobId.set(activeJobId);
    });
  }

  ngAfterViewInit(): void {
    if (this.job().jobId === this.activeJobId()) {
      setTimeout(() => {
        this.#elRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }

  ngAfterContentChecked(): void {
    this.setAnimation.set(
      this.vehicleDescriptionText()?.nativeElement?.offsetWidth > this.vehicleDescription()?.nativeElement?.offsetWidth
    );
  }

  saveActiveJob(jobId: number): void {
    this.#jobService.activeJobId = jobId;
    sessionStorage.setItem('previousUrl', 'jobViewer');
  }

  archiveJob(id: number): void {
    const data = {
      title: 'Archive',
      alertMessage: 'Are you sure you want to archive this claim?',
      confirmButton: 'ARCHIVE',
      hideCannotBeUndone: true,
      biggerButtons: true
    };
    this.#matDialog
      .open(ConfirmBoxComponent, this.#modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig))
      .afterClosed()
      .pipe(filter((value) => value === 'ARCHIVE'))
      .subscribe(() => {
        this.#toolbarService.setCloudState('SAVING');
        this.userService
          .archiveJobManually(id)
          .pipe(first())
          .subscribe({
            next: () => {
              this.#message.confirm('Job archived');
              this.#toolbarService.setCloudState('RESTING');
              this.getJobs.emit();
            },
            error: () => {
              this.#message.error('Could not archived the job.');
              this.#toolbarService.setCloudState('RESTING');
            }
          });
      });
  }

  toggleJobReadStatus(isUnread: boolean, id: number): void {
    this.userService.toggleJobReadStatus(isUnread, id).subscribe(() => this.getJobs.emit());
  }

  openRepairTimelineModal(job: Job): void {
    const data = {
      job,
      userTimezone: this.#currentUserService?.timeZone,
      repairTimelineDates: {
        authorityDate: job?.authorityDate,
        repairBookedDate: job?.repairBookedDate,
        vehicleDateIn: job?.vehicleDateIn,
        repairStartedDate: job?.repairStartedDate,
        repairCompletedDate: job?.repairCompletedDate,
        vehicleDateOut: job?.vehicleDateOut
      }
    };
    this.#matDialog
      .open(UpdateRepairTimelineComponent, this.#modalConfigsHelper.buildExtraWideModalConfig(data, 98))
      .afterClosed()
      .pipe(filter((result) => result === 'Success'))
      .subscribe(() => this.getJobs.emit());
  }
}
